// Sales and their undo (R13; D5: every sale channel pays 100%): cash, income and the ledger. Sales are manual
// income: total income and lifetime, never the automated share. Sales and undos are commands, so they run
// between ticks: after tick T, floor(T / bucketTicks) income buckets have closed (economySystem).
// Undoable sales: a counter / Sell-tool sale of one item (floor or machine slot) and a Crate "Sell all"
// (its counts go back into the Crate on undo).

import { applyPermille, floorDiv } from './core/num.ts';
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { itemValue, newId, removeItem, restoreItem } from './entities.ts';
import { book } from './ledger.ts';
import type { CrateB, FloorItem, SaleWhere } from './state.ts';

/** An undoable sale (counter, Sell tool, Crate "Sell all"): cash, lifetime, manual income, the sale count. */
function bookSale(c: Ctx, value: Int): void {
  c.s.wallet.cash += value;
  c.s.wallet.lifetime += value;
  c.s.income.accT += value;
  c.s.progress.sales += 1; // firstSale; an undo does not count down (unlocks never go back)
}

/** Sells items already taken off the board (records carry their origin cells); returns the token. */
export function sellTaken(c: Ctx, items: readonly FloorItem[], value: Int, where: SaleWhere): Int {
  for (const it of items) book(c.s, c.d, it.fam, it.tier, 'sold', 1);
  const token = newId(c.s);
  bookSale(c, value);
  c.s.transient.lastSale = {
    token,
    tick: c.s.tick,
    value,
    where,
    bid: null,
    items: items.map((x) => ({ id: x.id, fam: x.fam, tier: x.tier, x: x.x, y: x.y })),
    crate: null,
  };
  const at = items[0];
  c.ev.push({
    e: 'sell',
    token,
    value,
    where,
    ids: items.map((x) => x.id),
    x: at ? at.x : 0,
    y: at ? at.y : 0,
  });
  return token;
}

/** Sells loose items for `value` in total, records them as the undoable `lastSale`; returns its token. */
export function sellItems(c: Ctx, ids: readonly Int[], value: Int, where: SaleWhere): Int {
  const items: FloorItem[] = [];
  for (const id of ids) {
    const it = removeItem(c, id);
    items.push({ id: it.id, fam: it.fam, tier: it.tier, x: it.x, y: it.y });
  }
  return sellTaken(c, items, value, where);
}

/** Value of a Crate's contents at the "Sell all" rate. */
export function crateValue(c: Ctx, b: CrateB): Int {
  if (b.fam === null) return 0;
  let v = 0;
  for (let t = 1; t <= b.counts.length; t++) {
    const n = b.counts[t - 1] ?? 0;
    if (n > 0) v += n * itemValue(c.d, b.fam, t);
  }
  return applyPermille(v, c.d.economy.sellPermille.crateSellAll);
}

/** Crate "Sell all" (R7): an undoable sale of every count; the family clears. */
export function sellCrate(c: Ctx, b: CrateB): Int {
  const fam = b.fam;
  if (fam === null) throw new Error('sellCrate: empty crate');
  const value = crateValue(c, b);
  for (let t = 1; t <= b.counts.length; t++) {
    const n = b.counts[t - 1] ?? 0;
    if (n > 0) book(c.s, c.d, fam, t, 'sold', 1, n);
  }
  const token = newId(c.s);
  bookSale(c, value);
  c.s.transient.lastSale = {
    token,
    tick: c.s.tick,
    value,
    where: 'crate',
    bid: b.id,
    items: [],
    crate: { fam, counts: b.counts.slice() },
  };
  for (let i = 0; i < b.counts.length; i++) b.counts[i] = 0;
  b.fam = null;
  c.ev.push({ e: 'sell', token, value, where: 'crate', ids: [], x: b.x, y: b.y });
  return token;
}

/**
 * Takes the sale's income back out of the bucket it was booked into: the open accumulator, else its ring slot
 * while it is still in the window; once it has left the ring there is nothing to reverse. No bucket goes
 * negative, so sell/undo spam can neither inflate nor deflate income/s.
 */
function unbookIncome(c: Ctx, saleTick: Int, value: Int): void {
  const inc = c.s.income;
  const { bucketTicks, ringBuckets } = c.d.economy.income;
  const closed = floorDiv(c.s.tick, bucketTicks) - floorDiv(saleTick, bucketTicks);
  if (closed === 0) {
    inc.accT -= value;
  } else if (closed <= ringBuckets) {
    const slot = (inc.idx - closed + ringBuckets) % ringBuckets;
    inc.total[slot] = (inc.total[slot] ?? 0) - value;
  }
}

/**
 * Reverses the last sale (validated by rules/undo planUndoSell): items back onto the planned cells with
 * their ids, or a Crate sale's counts back into the Crate.
 */
export function undoSale(c: Ctx, cells: readonly { x: Int; y: Int }[]): void {
  const sale = c.s.transient.lastSale;
  if (sale === null) throw new Error('undoSale without a sale');
  const back: FloorItem[] = [];
  for (let i = 0; i < sale.items.length; i++) {
    const it = sale.items[i] as FloorItem;
    const at = cells[i];
    if (!at) throw new Error('undoSale: plan is shorter than the sale');
    const restored: FloorItem = { id: it.id, fam: it.fam, tier: it.tier, x: at.x, y: at.y };
    restoreItem(c, restored);
    book(c.s, c.d, it.fam, it.tier, 'sold', -1);
    back.push({ ...restored });
  }
  if (sale.crate !== null && sale.bid !== null) {
    const b = c.s.buildings.find((x) => x.id === sale.bid);
    if (b === undefined || b.type !== 'crate') throw new Error('undoSale: the crate is gone');
    b.fam = sale.crate.fam;
    for (let t = 1; t <= sale.crate.counts.length; t++) {
      const n = sale.crate.counts[t - 1] ?? 0;
      if (n === 0) continue;
      b.counts[t - 1] = (b.counts[t - 1] ?? 0) + n;
      book(c.s, c.d, sale.crate.fam, t, 'sold', -1, n);
    }
  }
  c.s.wallet.cash -= sale.value;
  c.s.wallet.lifetime -= sale.value;
  unbookIncome(c, sale.tick, sale.value);
  c.s.transient.lastSale = null;
  c.ev.push({ e: 'saleUndone', token: sale.token, value: sale.value, items: back });
}
